import React from 'react'
import classnames from 'classnames'
import map from 'lodash/map'
import find from 'lodash/find'
import getProjectStatusForProjectsList from '@Utils/GetProjectStatusForProjectsList'
import getShouldAllowToAddLanguages from '@Utils/GetShouldAllowToAddLanguages'
import getAllowLinkToEditor from '@Utils/GetAllowLinkToEditor'
import Button from '@SiteComponents/Button'
import Return from '@Images/languages_view_return.svg'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  IProjectLabelCaptions,
  IProject,
} from '@SiteModules/Project/types'

import './LanguagesViewHeader.scss'

const NUMBER_OF_LABELS_TO_SHOW = 5
const NUMBER_OF_LABELS_TO_SHOW_FULL_VIEW = 9

interface LanguagesViewHeaderProps {
  readonly fullLanguagesView: boolean
  readonly project: IProject | undefined
  readonly projectVersion: IProjectVersion | undefined
  readonly projectLabelCaptions: IProjectLabelCaptions[]
  resetOpenedProjectVersion(): void
  openSetupProjectOrRenewSubscriptionPopup(): void
}

const LanguagesViewHeader: React.FC<LanguagesViewHeaderProps> = props => {
  const {
    project,
    projectVersion,
    fullLanguagesView,
    projectLabelCaptions
  } = props

  const labelCaptionsOfProject = project ? find(projectLabelCaptions, projectLabelCaption => projectLabelCaption.projectId === project.id) : undefined

  const labelCaptions = labelCaptionsOfProject ? labelCaptionsOfProject.captions : []
  const numberOfLabelCaptions = labelCaptions.length
  const numberOfLabelsToShow = fullLanguagesView ? NUMBER_OF_LABELS_TO_SHOW_FULL_VIEW : NUMBER_OF_LABELS_TO_SHOW

  const shouldAllowAddingLanguages = projectVersion ? getShouldAllowToAddLanguages(projectVersion.attributes) : false
  const isDisabled = projectVersion ? projectVersion.attributes.isDisabled : false
  const transcript = projectVersion ? find(projectVersion.subtitles, subtitle => subtitle.attributes.transcription) : undefined
  const allowLinkToEditor = !!transcript && getAllowLinkToEditor(isDisabled, transcript.attributes)
  const shouldShowButtons = shouldAllowAddingLanguages || allowLinkToEditor

  const openEditor = () => {
    if (!allowLinkToEditor || !project || !projectVersion) { return }

    const urlParams = transcript && transcript.attributes.hasVoice ? '?openVoice=true' : ''
    window.location.href = `/projects/${project.id}/project_versions/${projectVersion.id}/subtitles/${transcript.id}/edit${urlParams}`
  }

  const renderTitleAndStatus = () => {
    const statuses = project ? map(project.projectVersions, pv => pv.attributes.status) : []
    const titleToRender = projectVersion && projectVersion.attributes.title || project && project.attributes.title || ''
    const [projectStatusClass, projectStatusToRender] = getProjectStatusForProjectsList(statuses)

    return (
      <div className="LanguagesViewHeader__title-status-wrapper">
        {fullLanguagesView && (
          <Return
            className="LanguagesViewHeader__return"
            onClick={props.resetOpenedProjectVersion}
          />
        )}
        <div className="LanguagesViewHeader__title">
          {titleToRender}
        </div>
        <div className={`LanguagesViewHeader__status-badge ProjectsTable__status-badge ${projectStatusClass}`}>
          {projectStatusToRender}
        </div>
      </div>
    )
  }

  const renderLanguagesAndLabels = () => {
    const numberOfLanguages = projectVersion ? projectVersion.subtitles.length : 0

    return (
      <div className="LanguagesViewHeader__languages-labels-wrapper">
        <div className="LanguagesViewHeader__languages">
          {`${numberOfLanguages} ${numberOfLanguages === 1 ? 'language' : 'languages'}`}
        </div>
        <ul className="LanguagesViewHeader__labels">
          {map(labelCaptions.slice(0, numberOfLabelsToShow), (caption, index) => {
            return (
              <li
                key={index}
                className="LanguagesViewHeader__label"
              >
                <div className="LanguagesViewHeader__label-caption">
                  {caption}
                </div>
              </li>
            )
          })}
          {numberOfLabelCaptions > numberOfLabelsToShow && (
            <li
              key={numberOfLabelsToShow}
              className="LanguagesViewHeader__label"
            >
              <div className="LanguagesViewHeader__label-caption">
                +{numberOfLabelCaptions - numberOfLabelsToShow}
              </div>
            </li>
          )}
        </ul>
      </div>
    )
  }

  return (
    <div
      className={classnames(
        'LanguagesViewHeader',
        { 'LanguagesViewHeader__full': fullLanguagesView}
      )}
    >
      {renderTitleAndStatus()}
      {renderLanguagesAndLabels()}
      {shouldShowButtons && (
        <div className="LanguagesViewHeader__buttons">
          {allowLinkToEditor && (
            <Button
              caption="Open editor"
              styleType="gray-subtle"
              size="large"
              onClick={openEditor}
            />
          )}
          {shouldAllowAddingLanguages && (
            <Button
              caption="Add language"
              styleType="brand"
              size="large"
              onClick={props.openSetupProjectOrRenewSubscriptionPopup}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default LanguagesViewHeader
